<template>
  <section>
    <div v-if="isLoading" class="my-4 text-center">
      <scale-loader></scale-loader>
    </div>
    <div v-else>
      <div v-if="!this.groups || this.groups.length == 0" class="mt-16">
        <p class="text-3xl text-center text-grey">
          <b-icon pack="fas" icon="ban" size="is-large"/>
          {{ $t('no_groups_to_select') }}
        </p>
      </div>
      <template v-else>
        <div
          class="epc-selection-page"
          :class="{ 'epc-selection-page--with-bg': showCorporateBackground && corporateBackgroundUrl }"
          :style="corporateBackgroundStyle"
        >
          <!-- Breadcrumb + view toggle -->
          <div class="epc-page-head">
            <div class="epc-breadcrumb">
              <span class="epc-crumb epc-crumb--home" @click="goHome">
                <b-icon pack="fas" icon="home" size="is-small"/>
              </span>
              <span v-for="(group, index) in breadcrumb" :key="group.id" class="epc-crumb-wrap">
                <b-icon pack="fas" icon="angle-right" size="is-small" class="epc-crumb-sep"/>
                <span class="epc-crumb" @click="goToLevel(index)">{{ group.name }}</span>
              </span>
            </div>

            <div class="epc-page-actions">
              <button
                type="button"
                class="epc-bg-toggle"
                :class="{ 'epc-bg-toggle--active': showCorporateBackground }"
                :disabled="!hasCorporateBackground"
                @click="toggleCorporateBackground"
              >
                <b-icon pack="fas" icon="image" size="is-small"/>
                <span>{{ $t('corporate_background') }}</span>
              </button>

              <div class="epc-viewtoggle">
                <button type="button" class="epc-view-btn epc-view-btn--active">
                  <b-icon pack="fas" icon="th-large" size="is-small"/>
                  <span>{{ $t('cards_view') }}</span>
                </button>
                <button type="button" class="epc-view-btn" @click="selectGroupMap">
                  <b-icon pack="fas" icon="map-marked-alt" size="is-small"/>
                  <span>{{ $t('map') }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Toolbar: search + archive -->
          <div class="epc-toolbar">
            <div class="epc-search">
              <span class="epc-search-icon"><b-icon pack="fas" icon="search" size="is-small"/></span>
              <input
                v-model="search"
                type="search"
                :placeholder="$t('search_group')"
                class="epc-search-input"
              />
            </div>

            <div class="epc-group-filter">
              <select v-model="filterGroupId" class="epc-group-filter-select">
                <option value="">{{ $t('all') }}</option>
                <option v-for="group in groupFilterOptions" :key="group.id" :value="group.id">
                  {{ groupFilterLabel(group) }}
                </option>
                <option value="ungrouped">{{ $t('without_group') }}</option>
              </select>
            </div>

            <div class="epc-archive">
              <v-switch
                v-model="showArchive"
                ref="show_archive"
                :label="$t('include_archive')"
                :color="showArchive ? 'green' : 'red'"
                v-on:checked="(value) => showArchive = value"
              />
            </div>
          </div>

          <!-- Groups with their projects -->
          <div v-if="displayGroups().length" class="epc-groups-list">
            <div v-for="group in displayGroups()" :key="group.id" class="epc-group-section">
              <div class="epc-group-head" :class="{ 'epc-group-head--link': hasGroupChildren(group) }"
                @click="onGroupHeadClick(group)"
              >
                <span class="epc-group-code" :class="groupColorClass(group)">{{ group.name }}</span>
                <span class="epc-group-name">{{ group.description }}</span>
                <span class="epc-group-count">{{ filteredGroupProjects(group).length }} {{ $t('projects') }}</span>
              </div>

              <div v-if="filteredGroupProjects(group).length" class="epc-grid">
                <div v-for="project in filteredGroupProjects(group)" :key="project.id"
                  class="epc-proj-card" :class="{ 'epc-proj-card--disabled': !project.can_participate }"
                >
                  <div class="epc-proj-img" @click="selectProject(project)">
                    <img v-if="project.image" :src="project.image" :alt="project.name" />
                    <div v-else class="epc-noimg">{{ $t('no_results') }}</div>
                    <div class="epc-img-scrim"></div>

                    <div class="epc-img-top">
                      <span class="epc-chips">
                        <!-- REDUNDANT <span class="epc-group-code epc-group-code--card" :class="groupColorClass(group)"> 
                          {{ group.name }}
                        </span> -->
                        <span v-show="project.can_view" class="epc-chip"><b-icon pack="fas" icon="eye" size="is-small"/></span>
                        <span v-show="project.can_collaborate" class="epc-chip"><b-icon pack="fas" icon="address-book" size="is-small"/></span>
                      </span>
                      <span v-if="project.notifications" class="epc-badge">
                        <b-icon pack="fas" icon="bell" size="is-small"/> {{ project.notifications }}
                      </span>
                    </div>

                    <div class="epc-img-bottom">
                      <div v-if="project.reference" class="epc-card-code">{{ project.reference }}</div>
                      <span class="epc-open"><b-icon pack="fas" icon="arrow-right" size="is-small"/></span>
                    </div>
                  </div>

                  <div class="epc-card-body">
                    <div class="epc-card-title" @click="selectProject(project)">{{ project.name }}</div>

                    <div class="epc-card-foot">
                      <b-dropdown v-show="project.can_participate">
                        <button class="button is-small" type="button" slot="trigger">
                          <template v-if="!project.user_notifications.project_notifications || project.user_notifications.project_notifications === 'all'">
                            <b-icon pack="far" icon="bell" class="float-left h-full text-grey"></b-icon>
                            <span class="text-grey">{{ $t('all_notifications') }}</span>
                          </template>
                          <template v-else>
                            <b-icon pack="far" icon="bell" class="float-left h-full text-grey"></b-icon>
                            <span class="text-grey">{{ $t('mentions_favorites') }}</span>
                          </template>
                          <b-icon pack="fas" class="text-grey" icon="angle-down"></b-icon>
                        </button>

                        <b-dropdown-item @click="setProjectNotifications(project, true)" value="all">
                          <div class="media">
                            <b-icon class="media-left" icon="globe-africa"></b-icon>
                            <div class="media-content">
                              <h3>{{ $t('all_notifications') }}</h3>
                              <small>{{ $t('all_notifications_text') }}</small>
                            </div>
                          </div>
                        </b-dropdown-item>

                        <b-dropdown-item @click="setProjectNotifications(project, false)" value="mentions_favorites">
                          <div class="media">
                            <b-icon class="media-left" icon="user-check"></b-icon>
                            <div class="media-content">
                              <h3>{{ $t('mentions_favorites') }}</h3>
                              <small>{{ $t('mentions_favorites_text') }}</small>
                            </div>
                          </div>
                        </b-dropdown-item>
                      </b-dropdown>

                      <span class="epc-open-label" @click="selectProject(project)">
                        {{ $t('open') }} <b-icon pack="fas" icon="angle-right" size="is-small"/>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="epc-empty epc-empty--inline">
                <p>{{ $t('no_results') }}</p>
              </div>
            </div>
          </div>

          <!-- Ungrouped projects -->
          <div v-if="showUngroupedProjects"
            class="epc-group-section epc-projects-section">

            <div class="epc-group-head">
              <span class="epc-group-code epc-group-code--slate">{{ $t('projects_without_group_title') }}</span>
              <span class="epc-group-name">{{ $t('projects_without_group_subtitle') }}</span>
              <span class="epc-group-count">{{ filteredProjects(projects).length }} {{ $t('projects') }}</span>
            </div>

            <!-- No results -->
            <div v-if="filteredProjects(projects).length == 0" class="epc-empty">
              <b-icon pack="fas" icon="search" size="is-large"/>
              <p>{{ $t('no_results') }}</p>
            </div>

            <!-- Projects grid -->
            <div v-else class="epc-grid">
              <div
                v-for="project in filteredProjects(projects)"
                :key="project.id"
                class="epc-proj-card"
                :class="{ 'epc-proj-card--disabled': !project.can_participate }"
              >
                <!-- Image + overlay -->
                <div class="epc-proj-img" @click="selectProject(project)">
                  <img v-if="project.image" :src="project.image" :alt="project.name" />
                  <div v-else class="epc-noimg">{{ $t('no_results') }}</div>
                  <div class="epc-img-scrim"></div>

                  <!-- top row: permission chips + notifications badge -->
                  <div class="epc-img-top">
                    <span class="epc-chips">
                      <span v-show="project.can_view" class="epc-chip"><b-icon pack="fas" icon="eye" size="is-small"/></span>
                      <span v-show="project.can_collaborate" class="epc-chip"><b-icon pack="fas" icon="address-book" size="is-small"/></span>
                    </span>
                    <span v-if="project.notifications" class="epc-badge">
                      <b-icon pack="fas" icon="bell" size="is-small"/> {{ project.notifications }}
                    </span>
                  </div>

                  <!-- bottom overlay: area -->
                  <div class="epc-img-bottom">
                    <!-- <div v-if="project.area_name" class="epc-card-code">{{ project.area_name }}</div> -->
                    <div v-if="project.reference" class="epc-card-code">{{ project.reference }}</div>
                    <span class="epc-open"><b-icon pack="fas" icon="arrow-right" size="is-small"/></span>
                  </div>
                </div>

                <!-- Body -->
                <div class="epc-card-body">
                  <div class="epc-card-title" @click="selectProject(project)">{{ project.name }}</div>

                  <!-- <div class="epc-info-row">
                    <b-icon pack="fas" icon="wallet" size="is-small" class="epc-info-icon"/>
                    <span class="epc-info-label">{{ $t('budget') }}</span>
                    <span class="epc-info-value">
                      <template v-if="project.budget">{{ formatPrice(project.budget.toFixed(2)) }} {{ project.currency }}</template>
                      <template v-else>N/A</template>
                    </span>
                  </div> -->

                  <!-- <div class="epc-info-row">
                    <b-icon pack="fas" icon="calendar" size="is-small" class="epc-info-icon"/>
                    <span class="epc-info-label">{{ $t('delivery_date') }}</span>
                    <span class="epc-info-value">{{ (new Date(project.current_end_date)).toLocaleDateString() }}</span>
                  </div> -->

                  <!-- Footer: notifications dropdown -->
                  <div class="epc-card-foot">
                    <b-dropdown v-show="project.can_participate">
                      <button class="button is-small" type="button" slot="trigger">
                        
                        <template v-if="!project.user_notifications.project_notifications || project.user_notifications.project_notifications === 'all'">
                          <b-icon pack="far" icon="bell" class="float-left h-full text-grey"></b-icon>
                          <span class="text-grey">{{ $t('all_notifications') }}</span>
                        </template>
                        <template v-else>
                          <b-icon pack="far" icon="bell" class="float-left h-full text-grey"></b-icon>
                          <span class="text-grey">{{ $t('mentions_favorites') }}</span>
                        </template>
                        <b-icon pack="fas" class="text-grey" icon="angle-down"></b-icon>

                      </button>

                      <b-dropdown-item @click="setProjectNotifications(project, true)" value="all">
                        <div class="media">
                          <b-icon class="media-left" icon="globe-africa"></b-icon>
                          <div class="media-content">
                            <h3>{{ $t('all_notifications') }}</h3>
                            <small>{{ $t('all_notifications_text') }}</small>
                          </div>
                        </div>
                      </b-dropdown-item>

                      <b-dropdown-item @click="setProjectNotifications(project, false)" value="mentions_favorites">
                        <div class="media">
                          <b-icon class="media-left" icon="user-check"></b-icon>
                          <div class="media-content">
                            <h3>{{ $t('mentions_favorites') }}</h3>
                            <small>{{ $t('mentions_favorites_text') }}</small>
                          </div>
                        </div>
                      </b-dropdown-item>

                    </b-dropdown>

                    <span class="epc-open-label" @click="selectProject(project)">
                      {{ $t('open') }} <b-icon pack="fas" icon="angle-right" size="is-small"/>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from "axios";
import ScaleLoader from "vue-spinner/src/ScaleLoader.vue";

export default {
  middleware: "auth",
  title: "select_group_page_title",
  subtitle: "select_group_page_subtitle",
  menuOption: "3-0",
  components: {
    ScaleLoader
  },

  data: () => ({
    s3bucket: window.config.s3bucket,
    groups: null,
    projects: null,
    groupProjects: null,
    isLoading: true,
    search: '',
    showArchive: 0,
    breadcrumb: [],
    selectedGroup: null,
    showCorporateBackground: localStorage.getItem('epc-show-corporate-background') === '1',
    filterGroupId: '',
  }),

  watch: {
    showArchive(value) {
      this.isLoading = true
      this.getGroups()
    }
  },

  computed: {
    ...mapGetters({
      company: 'app/company',
    }),

    corporateBackgroundUrl() {
      if (!this.company || !this.company.image_background || !this.company.alias) {
        return null
      }

      const bg = this.company.image_background

      // Already a full URL (e.g. persisted Vuex from older API) — use as-is
      if (/^https?:\/\//i.test(bg) || bg.startsWith('//')) {
        return bg
      }

      return '//' + this.s3bucket + '/' + this.company.alias + '/' + bg
    },

    hasCorporateBackground() {
      return !!this.corporateBackgroundUrl
    },

    corporateBackgroundStyle() {
      if (!this.showCorporateBackground || !this.corporateBackgroundUrl) {
        return {}
      }

      return { '--epc-bg-image': `url("${this.corporateBackgroundUrl}")` }
    },

    groupFilterOptions() {
      return this.flattenGroups(this.groups || [])
    },

    showUngroupedProjects() {
      if (!this.projects || this.projects.length === 0) {
        return false
      }

      if (this.filterGroupId === 'ungrouped') {
        return true
      }

      return !this.filterGroupId && this.breadcrumb.length === 0
    },
  },

  created() {
    // solo cargara los grupos si se ha seleccionado una compania, caso contrario
    // en el mounted mostramos mensaje y redireccionamos al apartado correspondiente
    if (this.company)
      this.getGroups();
  },

  mounted() {
    if (!this.company) {
      this.$notify.info('Debe seleccionar una empresa')
      this.$router.push({ name: "company.select" });
    }
  },

  methods: {

    async getGroups() {
      const { data } = await axios.get("/api/v2/user/groups", {
        'params' : { showArchive: this.showArchive, ungroupedProjects: 1, withProjects: 1, companyId: this.company.id }
      });

      if (data.groupsWithAccess && data.groupsWithAccess.length > 0) {
        const breadcrumbIds = this.breadcrumb.map(group => group.id)
        this.groups = data.groupsWithAccess;

        if (data.projectsWithoutGroups) {
          this.projects = data.projectsWithoutGroups;
        }

        if (breadcrumbIds.length > 0) {
          this.breadcrumb = this.resolveBreadcrumb(this.groups, breadcrumbIds)
          this.selectedGroup = this.breadcrumb[this.breadcrumb.length - 1] || null
        }

        this.isLoading = false;
      }else{
        this.$router.push({ name: "project.select" });
      }
    },

    selectGroup(group) {
      if (!this.breadcrumb || this.breadcrumb.length === 0) {
        this.breadcrumb = [group]
      } else {
        const idx = this.breadcrumb.findIndex(g => g.id === group.id)
        if (idx !== -1) {
          this.breadcrumb = this.breadcrumb.slice(0, idx + 1)
        } else {
          this.breadcrumb.push(group)
        }
      }

      this.selectedGroup = this.breadcrumb[this.breadcrumb.length - 1] || null
    },

    goToLevel(index) {
      // cortar el breadcrumb hasta el nivel clicado
      this.breadcrumb = this.breadcrumb.slice(0, index + 1)
      this.selectedGroup = this.breadcrumb[index]
    },

    goHome() {
      this.breadcrumb = []
      this.selectedGroup = null
    },

    onGroupHeadClick(group) {
      if (this.hasGroupChildren(group)) {
        this.selectGroup(group)
      }
    },

    hasGroupChildren(group) {
      return group && Array.isArray(group.children) && group.children.length > 0
    },

    groupColorClass(group) {
      const colors = [
        'epc-group-code--blue',
        'epc-group-code--green',
        'epc-group-code--purple',
        'epc-group-code--orange',
        'epc-group-code--teal',
      ]
      return colors[group.id % colors.length]
    },

    displayGroups() {
      if (this.filterGroupId === 'ungrouped') {
        return []
      }

      if (this.filterGroupId) {
        const filterId = Number(this.filterGroupId)
        const found = this.flattenGroups(this.groups || []).find(group => group.id === filterId)
        return found ? [found] : []
      }

      let list = []

      if (this.breadcrumb && this.breadcrumb.length > 0 && this.selectedGroup) {
        list.push(this.selectedGroup)
      }

      list = list.concat(this.filteredGroups())

      return list.filter(group => this.shouldShowGroupSection(group))
    },

    flattenGroups(groups, result = []) {
      for (const group of groups) {
        result.push(group)
        if (Array.isArray(group.children) && group.children.length > 0) {
          this.flattenGroups(group.children, result)
        }
      }
      return result
    },

    groupFilterLabel(group) {
      if (group.description) {
        return group.name + ' — ' + group.description
      }
      return group.name
    },

    shouldShowGroupSection(group) {
      const projects = this.filteredGroupProjects(group)
      const q = (this.search || '').toLowerCase()

      if (projects.length > 0) {
        return true
      }

      if (!q && this.hasGroupChildren(group)) {
        return true
      }

      if (q && this.groupMatchesSearch(group)) {
        return true
      }

      return false
    },

    groupMatchesSearch(group) {
      const q = (this.search || '').toLowerCase()
      if (!q) return true
      if (group.name && group.name.toLowerCase().includes(q)) return true
      if (group.description && group.description.toLowerCase().includes(q)) return true
      if (group.code && String(group.code).toLowerCase().includes(q)) return true
      return this.filteredGroupProjects(group).length > 0
    },

    filteredGroupProjects(group) {
      if (!group || !group.projects) return []

      const q = (this.search || '').toLowerCase()

      return group.projects.filter(project => {
        if (!q) return true

        const nameMatch = project.name && project.name.toLowerCase().includes(q)
        const referenceMatch = project.reference && project.reference.toLowerCase().includes(q)

        return nameMatch || referenceMatch
      }).sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
    },

    async selectGroup2(group) {
      await this.$store.dispatch("app/setGroup", { group });
      this.$router.push({ name: "project.select" });
    },

    filteredGroups2() {
      return this.groups.filter(group => {
        return group.name.toLowerCase().includes(this.search.toLowerCase())
      })
    },

    filteredGroups() {
      // Orden alfanumérico ascendente por nombre: números por valor y antes que las letras
      // (numeric:true → "2" < "10"), insensible a mayúsculas/acentos.
      const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })

      // Si no hay breadcrumb -> estamos en Home: mostrar grupos raíz
      if (!this.breadcrumb || this.breadcrumb.length === 0) {
        return (this.groups || []).filter(group => this.groupMatchesSearch(group)).sort(byName)
      }

      // Estamos dentro de algún grupo (breadcrumb)
      const current = this.breadcrumb[this.breadcrumb.length - 1]
      if (!current) return []

      const children = Array.isArray(current.children) ? current.children : []
      return children.filter(child => this.groupMatchesSearch(child)).sort(byName)
    },

    resolveBreadcrumb(groups, ids) {
      let current = groups
      const result = []

      for (const id of ids) {
        const found = current.find(group => group.id === id)
        if (!found) break
        result.push(found)
        current = found.children || []
      }

      return result
    },

    async getProjects() {
      if (!this.selectedGroup) {
        return
      }
      try {
        this.groupProjects = []
        const { data } = await axios.get("/api/v2/user/projects", {
          params: {
            showArchive: this.showArchive,
            group: this.selectedGroup.id,
            companyId: this.company.id,
            withConfigs: 1
          }
        })

        this.groupProjects = data.projects || []
      } catch (error) {
        console.error("Error al cargar proyectos:", error)
      }
    },

    async selectGroupMap() {
      await this.$router.push({ name: "group.select.map" });
    },

    toggleCorporateBackground() {
      if (!this.hasCorporateBackground) {
        return
      }

      this.showCorporateBackground = !this.showCorporateBackground
      localStorage.setItem('epc-show-corporate-background', this.showCorporateBackground ? '1' : '0')
    },

    async selectProject(project) {
      await this.$store.dispatch("app/setProject", { project });
      let tags = project.tags
      await this.$store.dispatch("app/setTags", { tags });
      this.$router.push({ name: "project.home" });
    },

    async setProjectNotifications(project, value) {
      // params
      let params = {
        key: "project_notifications",
        value: value ? "all" : "mentions_favorite",
        project: project.id
      };

      // endpoint
      const { data } = await axios.post(
        "/api/v2/project/user/notifications",
        params
      );

      if (data.success) {
        this.getGroups();
        this.$notify.success("success_change_user_project_notifications");
      } else {
        // show error msg
        this.$notify.error("error_change_user_project_notifications");
      }
    },

    filteredProjects(projects) {
      if (!projects) return []
      const q = (this.search || '').toLowerCase()
      return projects.filter(project => {
        const nameMatch = project.name && project.name.toLowerCase().includes(q)
        const referenceMatch = project.reference && project.reference.toLowerCase().includes(q)
        return !q || nameMatch || referenceMatch
      }).sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
    },

    formatPrice(value) {
        let val = (value/1).toFixed(2).replace('.', ',')
        return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    }


  }
};
</script>

<style lang="scss" scoped>
@import '#/assets/sass/front/components/project-selection.scss';
</style>
